/**
 * Kâr-zarar — KK-605, KK-609, KK-608.
 *
 * =============================================================================
 * EKRANIN TEK İŞİ: "HANGİ NOKTA ZARAR EDİYOR"
 * -----------------------------------------------------------------------------
 * KK-609 bunu tek ekrandan cevaplamayı şart koşar. Bu yüzden:
 *   · Sunucu satırları NET KÂRA GÖRE ARTAN döndürür; zarar eden nokta listenin
 *     BAŞINDADIR ve ekran bu sırayı BOZMAZ.
 *   · Zarar varsa tablonun üstünde açık bir uyarı bandı çıkar; kullanıcıdan
 *     sütun tarayıp bulması beklenmez.
 *   · Grafik nokta eksenlidir: karşılaştırma zaman değil, YER üzerinedir.
 *
 * PARA: gelir/gider birleştirmeleri @kantin/shared'daki paraTopla() ile
 * yapılır; hiçbir yerde Number()/parseFloat() yoktur (ADR-03).
 *
 * SÜTUN RENGİ: gider ve maliyet sütunları YÖNSÜZ gösterilir — bunlar birer
 * defter satırı değil, dönem toplamıdır ve başlıkları zaten yönü söyler.
 * Yön kodlaması NET KÂR sütununda çalışır: eksi net, kırmızı mürekkeptir.
 * =============================================================================
 */
import { useEffect, useState } from "react";
import {
  Rozet,
  Tablo,
  TabloBaslik,
  TabloGovde,
  Tutar,
  Uyari,
} from "@kantin/ui";
import { KarZarar } from "@kantin/ui/grafik";
import type { KarZararNoktasi } from "@kantin/ui/grafik";
import { para, paraStr, paraTopla, yerelGun } from "@kantin/shared";
import { oturumDurumu } from "../../veri/oturum.js";
import { useKarZarar, useOkul } from "../../veri/sorgular.js";
import type { KarZararSatiri } from "../../veri/tipler.js";
import { ayinIlkGunu, yerelBugun } from "../../veri/bicim.js";
import {
  DisaAktar,
  FiltreSeridi,
  Sayfa,
  SayfaBasligi,
  SorguDurumu,
  TarihAlani,
} from "../../ortak.js";

export function KarZararRaporu() {
  const seciliNoktaId = oturumDurumu((s) => s.seciliNoktaId);
  const okul = useOkul();
  const [bas, setBas] = useState(ayinIlkGunu(yerelBugun()));
  const [bit, setBit] = useState(yerelBugun());
  const [dokunuldu, setDokunuldu] = useState(false);

  useEffect(() => {
    if (dokunuldu || !okul.data) return;
    const gun = yerelGun(new Date(), okul.data.saat_dilimi);
    setBas(ayinIlkGunu(gun));
    setBit(gun);
  }, [okul.data, dokunuldu]);

  const rapor = useKarZarar(bas, bit, seciliNoktaId);
  const satirlar = rapor.data?.noktalar ?? [];
  const zarardakiler = satirlar.filter((n) => para(n.net_kar).isNegative());

  return (
    <Sayfa>
      <SayfaBasligi
        baslik="Kâr-zarar"
        aciklama="Satış geliri − mal maliyeti (satış anındaki alış fiyatıyla) − giderler + diğer gelirler. Nokta bazlıdır."
        eylem={
          <DisaAktar
            rapor="kar-zarar"
            dosyaKoku={`kar-zarar-${bas}_${bit}`}
            sorgu={{ bas, bit, nokta_id: seciliNoktaId ?? undefined }}
            devreDisi={!bas || !bit}
          />
        }
      />

      <FiltreSeridi>
        <TarihAlani
          id="kz-bas"
          etiket="Başlangıç"
          deger={bas}
          degistir={(v) => {
            setDokunuldu(true);
            setBas(v);
          }}
        />
        <TarihAlani
          id="kz-bit"
          etiket="Bitiş"
          deger={bit}
          degistir={(v) => {
            setDokunuldu(true);
            setBit(v);
          }}
        />
        {seciliNoktaId ? (
          <Rozet tur="simit">nokta süzgeci sunucuda uygulanıyor</Rozet>
        ) : (
          <p className="m-0 max-w-prose text-xs text-murekkep-3">
            Nokta süzgeci kapalı: noktaya atanmamış genel giderler (kira,
            muhasebe) de listede yer alır.
          </p>
        )}
      </FiltreSeridi>

      <SorguDurumu
        yukleniyor={rapor.isLoading}
        hata={rapor.error}
        bos={!rapor.isLoading && satirlar.length === 0}
        bosBaslik="Bu aralıkta hareket yok"
        bosAciklama="Seçili dönemde satış, gider veya gelir kaydı bulunamadı."
        iskeletSatir={6}
        yenile={() => void rapor.refetch()}
      >
        {rapor.data && (
          <>
            {zarardakiler.length > 0 ? (
              <Uyari tur="hata" baslik="Zarar eden nokta var">
                {zarardakiler.map((n) => n.nokta_ad).join(", ")} bu dönemde
                zarar etti. Satırlar net kâra göre sıralıdır; zarar eden nokta
                listenin başındadır.
              </Uyari>
            ) : (
              <Uyari tur="onay" baslik="Zarar eden nokta yok">
                Seçili dönemde tüm noktaların net kârı sıfır veya üzerindedir.
              </Uyari>
            )}

            <KarZarar veri={grafikVerisi(satirlar)} />

            <Tablo>
              <TabloBaslik>
                <th scope="col">Nokta</th>
                <th scope="col">Satış geliri</th>
                <th scope="col">Mal maliyeti</th>
                <th scope="col">Brüt kâr</th>
                <th scope="col">Brüt marj</th>
                <th scope="col">Diğer gelir</th>
                <th scope="col">Gider</th>
                <th scope="col">Net kâr</th>
              </TabloBaslik>
              <TabloGovde>
                {satirlar.map((n) => (
                  <tr key={n.nokta_id ?? "genel"}>
                    <th scope="row" className="px-3 py-2 text-left font-normal">
                      {n.nokta_ad}
                      {n.nokta_id === null && (
                        <span className="ml-2">
                          <Rozet tur="notr">noktasız</Rozet>
                        </span>
                      )}
                    </th>
                    <td className="text-right">
                      <Tutar deger={n.satis_geliri} yonsuz boy="sm" />
                    </td>
                    <td className="text-right">
                      <Tutar deger={n.mal_maliyeti} yonsuz boy="sm" />
                    </td>
                    <td className="text-right">
                      <Tutar deger={n.brut_kar} boy="sm" etiket="brüt kâr" />
                    </td>
                    <td className="rakam text-right font-mono">
                      {n.brut_marj_yuzde ? `%${n.brut_marj_yuzde}` : "—"}
                    </td>
                    <td className="text-right">
                      <Tutar deger={n.diger_gelir} yonsuz boy="sm" />
                    </td>
                    <td className="text-right">
                      <Tutar deger={n.gider} yonsuz boy="sm" />
                    </td>
                    <td className="text-right">
                      <Tutar
                        deger={n.net_kar}
                        isaretiGoster
                        boy="lg"
                        etiket={`${n.nokta_ad} net kârı`}
                      />
                    </td>
                  </tr>
                ))}
                <tr className="bg-kagit-2 font-semibold">
                  <th scope="row" className="px-3 py-2 text-left">
                    Konsolide
                  </th>
                  <td className="text-right">
                    <Tutar
                      deger={rapor.data.konsolide.satis_geliri}
                      yonsuz
                      boy="sm"
                    />
                  </td>
                  <td className="text-right">
                    <Tutar
                      deger={rapor.data.konsolide.mal_maliyeti}
                      yonsuz
                      boy="sm"
                    />
                  </td>
                  <td className="text-right">
                    <Tutar deger={rapor.data.konsolide.brut_kar} boy="sm" />
                  </td>
                  <td className="rakam text-right font-mono">
                    {rapor.data.konsolide.brut_marj_yuzde
                      ? `%${rapor.data.konsolide.brut_marj_yuzde}`
                      : "—"}
                  </td>
                  <td className="text-right">
                    <Tutar
                      deger={rapor.data.konsolide.diger_gelir}
                      yonsuz
                      boy="sm"
                    />
                  </td>
                  <td className="text-right">
                    <Tutar deger={rapor.data.konsolide.gider} yonsuz boy="sm" />
                  </td>
                  <td className="text-right">
                    <Tutar
                      deger={rapor.data.konsolide.net_kar}
                      isaretiGoster
                      boy="lg"
                      etiket="konsolide net kâr"
                    />
                  </td>
                </tr>
              </TabloGovde>
            </Tablo>

            <p className="m-0 max-w-prose text-xs text-murekkep-3">
              Fire ve imha maliyeti bu rapora gider kaleminden girer; stok
              hareketlerinden ayrıca türetilmez (KK-611 — çift sayım olurdu).
              Fire'nin miktar kırılımı Nokta sorumluluğu raporundadır.
            </p>
          </>
        )}
      </SorguDurumu>
    </Sayfa>
  );
}

// ---------------------------------------------------------------------------

/**
 * Grafik için dönem ekseni NOKTADIR: KK-609'un sorusu "ne zaman" değil
 * "nerede" zarar edildiğidir. Gelir = satış geliri + diğer gelir,
 * gider = mal maliyeti + gider; ikisi de paraTopla() ile birleştirilir.
 */
function grafikVerisi(satirlar: KarZararSatiri[]): KarZararNoktasi[] {
  return satirlar.map((n) => ({
    donem: n.nokta_ad,
    gelir: paraStr(paraTopla(n.satis_geliri, n.diger_gelir)),
    gider: paraStr(paraTopla(n.mal_maliyeti, n.gider)),
    net: n.net_kar,
  }));
}
